import { CommonModule } from '@angular/common';
import { Component, DestroyRef, effect, Inject, OnInit, OnDestroy, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { NgHttpCachingService } from 'ng-http-caching';
import { map, take } from 'rxjs';

import {
	DeviceClaimingApiService,
	DeviceDiscoveryApiService,
	ScheduledJobsApiService
} from '@shure/cloud/device-management/devices/data-access';
import { ElevatedAccessMainComponent } from '@shure/cloud/shared/elevated-access/feature-elevated-access';
import { OktaInterfaceService, monitorLoginState } from '@shure/cloud/shared/okta/data-access';
import {
	OrganizationSwitcherComponent,
	OrganizationSwitcherService
} from '@shure/cloud/shared/organization-switcher/feature-organization-switcher';
import { BreakpointService } from '@shure/cloud/shared/services/media-breakpoints';
import { OrganizationsStoreService } from '@shure/cloud/shared/services/organizations-store-service';
import { SecondaryNavService } from '@shure/cloud/shared/services/secondary-nav-service';
import { RealTimeInAppBrowserNotificationService } from '@shure/cloud/shared/services/websocket';
import {
	FooterModule,
	HeaderToolbarModule,
	SecondaryNavList,
	ShSecondaryNavComponent,
	SideNavComponent
} from '@shure/cloud/shared/ui/components';
import { ApiUsersService } from '@shure/cloud/shared/users/data-access';
import { AnalyticsEvents } from '@shure/cloud/shared/utils/analytics';
import { FEATURE_FLAG_SERVICE, CloudFeatureFlagsService } from '@shure/cloud/shared/utils/feature-flags';
import { PrismLayoutComponent, PrismNavComponent, PrismSnackbarService } from '@shure/prism-angular-components';
import { AnalyticsService } from '@shure/shared/angular/utils/analytics';
import { ILogger } from '@shure/shared/angular/utils/logging';
import { asLabel, SignOutEvent } from '@shure/shared/models';

@Component({
	selector: 'sh-app-shell',
	imports: [
		CommonModule,
		FooterModule,
		ElevatedAccessMainComponent,
		HeaderToolbarModule,
		PrismLayoutComponent,
		PrismNavComponent,
		RouterModule,
		ShSecondaryNavComponent,
		SideNavComponent,
		OrganizationSwitcherComponent,
		TranslocoModule
	],
	templateUrl: './app-shell.component.html',
	styleUrl: './app-shell.component.scss'
})
export class AppShellComponent implements OnInit, OnDestroy {
	public readonly isSmallDevice = this.breakpointService.isLteSmall;
	protected readonly secondaryNavListItems = this.secondaryNavService.secondaryNavListItems;
	public organizationKnownAndSet = signal<boolean>(false);
	public defaultOrgId = toSignal(this.oktaIntf.getOrganizationId$());
	private secondaryNavItems: SecondaryNavList[] = [
		{
			id: 'devices',
			key: 'devices',
			feature: '',
			path: 'devices',
			listItem: asLabel('cloud.device-management.navitems.devices'),
			icon: 'sh-network',
			keyInParams: false
		},
		{
			id: 'pending-devices',
			key: 'pending-devices',
			feature: '',
			path: 'pending-devices',
			listItem: asLabel('cloud.device-management.navitems.pending-devices'),
			icon: 'help',
			keyInParams: false
		},
		{
			id: 'scheduled',
			key: 'scheduled',
			feature: '',
			path: 'scheduled',
			listItem: asLabel('cloud.device-management.navitems.scheduled'),
			icon: 'access_time',
			keyInParams: false
		}
	];

	private deviceCount = toSignal(this.discoveryService.discoveredDevicesCountAfterDiscovery$());
	private pendingClaimCount = toSignal(
		this.deviceClaimService.getPendingClaims$().pipe(map((pendingClaims) => pendingClaims.length))
	);
	private scheduledJobCount = toSignal(this.scheduledJobsService.getScheduledJobsCount$());

	constructor(
		private readonly router: Router,
		private readonly activatedRoute: ActivatedRoute,
		private readonly organizationSwitcherService: OrganizationSwitcherService,
		@Inject(PrismSnackbarService) private readonly snackbarService: PrismSnackbarService,
		private readonly translocoService: TranslocoService,
		public readonly oktaIntf: OktaInterfaceService,
		public readonly breakpointService: BreakpointService,
		private readonly destroyRef: DestroyRef,
		private readonly secondaryNavService: SecondaryNavService,
		private ngHttpCachingService: NgHttpCachingService,
		private readonly logger: ILogger,
		private readonly discoveryService: DeviceDiscoveryApiService,
		private readonly deviceClaimService: DeviceClaimingApiService,
		private readonly analyticsService: AnalyticsService,
		@Inject(FEATURE_FLAG_SERVICE) private readonly featureFlagService: CloudFeatureFlagsService,
		private readonly scheduledJobsService: ScheduledJobsApiService,
		private orgStoreService: OrganizationsStoreService,
		private apiUsersService: ApiUsersService,
		private realTimeInAppBrowserNotificationService: RealTimeInAppBrowserNotificationService
	) {
		this.logger = logger.createScopedLogger('CDM AppShell');

		// Setup secondary nav
		this.secondaryNavService.setSecondaryNavListItems(this.secondaryNavItems);

		effect(() => {
			this.updateSecondaryNavItem('devices', this.deviceCount());
		});

		effect(() => {
			this.updateSecondaryNavItem('scheduled', this.scheduledJobCount());
		});

		effect(() => {
			this.updateSecondaryNavItem('pending-devices', this.pendingClaimCount());
		});

		monitorLoginState(oktaIntf, {
			onLogIn: this.onLogIn.bind(this),
			onLogOut: this.onLogOut.bind(this)
		});
	}

	public ngOnInit(): void {
		this.logger.debug('CDM', 'ngOnInit');
	}

	public ngOnDestroy(): void {
		this.realTimeInAppBrowserNotificationService.disconnectWebSocket();
	}

	public onLogIn(): void {
		this.ngHttpCachingService.clearCache();

		this.organizationSwitcherService
			.handleOrgIdQueryParameter()
			.pipe(take(1))
			.subscribe(() => {
				this.organizationKnownAndSet.set(true);
			});
	}

	public onLogOut(): void {
		this.logger.debug('CDM', 'onLogOut');
	}

	public sendSignOutAnalyticsEvent(): void {
		// removing sign in state so that on next login we can track sign in again
		localStorage.removeItem(AnalyticsEvents.SignIn);
		this.analyticsService.trackEvent(new SignOutEvent());
	}

	private updateSecondaryNavItem(name: string, count: number | undefined): void {
		this.secondaryNavService.updateSecondaryNavListItem(name, {
			listItemPostfix: count !== undefined && count > 0 ? `(${count})` : undefined
		});
	}
}
